import React, {FC} from 'react';
import {Text} from '@chakra-ui/react';
import {Flex} from '@chakra-ui/layout';
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
} from 'react-share';
import Image from 'next/image';
import {useReactToPrint} from 'react-to-print';
import {ModalTypes, useModalContext} from '..';
import {baseUrl, getGeneratedImageUrl} from '../../../utils/utils';
import {theme} from '../../../style/theme';
import {translate} from './content';

interface IOptions {
  isShareable?: boolean;
  isDownloadable?: boolean;
  isPrintable?: boolean;
  isWalletPass?: boolean;
}

export const actions: Record<ModalTypes, IOptions> = {
  certificate: {isDownloadable: true},
  redeem: {isDownloadable: true},
  share: {isShareable: true},
  wallet: {isWalletPass: false},
  deed: {isDownloadable: true},
};

const Print = () => {
  const handlePrint = useReactToPrint({
    content: () => document.getElementById('certificate') || null,
  });

  return (
    <Flex
      onClick={handlePrint}
      maxW={{base: '25px', md: '33px'}}
      flexDirection="column"
      css={{'&:hover': {cursor: 'pointer'}}}
    >
      <Image
        src="/assets/icon-printer.svg"
        width="100%"
        height="100%"
        alt="printer icon"
      />
      <Text
        textStyle="body3"
        color={theme.colors.white}
        display={{base: 'none', md: 'revert'}}
      >
        Print
      </Text>
    </Flex>
  );
};

export const DownloadButton: FC<{type: string; url?: string}> = ({
  type,
  url,
}) => {
  const text: {[key: string]: string} = {
    certificate: 'Download Certifcate',
    deed: 'Download Deed',
    redeem: 'Download',
  };

  return (
    <Flex flexDirection="column">
      <a
        target={['deed', 'wallet'].includes(type) ? '_blank' : ''}
        href={url}
        download
        rel="noreferrer"
      >
        {actions[type as keyof typeof actions].isDownloadable && (
          <Flex width="100%" justifyContent="center">
            <Flex maxW={{base: '25px', md: '33px'}}>
              <Image
                src="/assets/icon-download-white.svg"
                width="100%"
                height="100%"
              />
            </Flex>
          </Flex>
        )}

        {actions[type as keyof typeof actions].isWalletPass && (
          <Flex width="100%" justifyContent="center">
            <Flex maxW={{base: '25px', md: '250px'}}>
              <Image
                src="/assets/add-to-apple-wallet-icon.svg"
                width="100%"
                height="100%"
              />
            </Flex>
          </Flex>
        )}

        <Text
          textAlign="center"
          textStyle={{base: 'downloadTextSm', md: 'downloadTextLg'}}
          color={theme.colors.white}
          w="fit-content"
        >
          {text[type]}
        </Text>
      </a>
    </Flex>
  );
};

const Download = () => {
  const {
    type,
    userId,
    assetId,
    signature,
    transactionId,
    isDeed,
    disableDeedDownload,
  } = useModalContext();

  const downloadOptions: Record<ModalTypes, any> = {
    certificate: {
      userId,
      assetId,
      signature,
    },
    share: {
      userId,
      assetId,
      signature,
    },
    deed: {
      transactionId,
    },
    redeem: {
      transactionId,
    },
    wallet: {
      userId,
      assetId,
    },
  };

  return (
    <>
      {isDeed || type === 'certificate' ? (
        <Flex>
          <DownloadButton
            type="certificate"
            url={getGeneratedImageUrl(translate[type], downloadOptions[type])}
          />
          <DownloadButton
            type="wallet"
            url={getGeneratedImageUrl('wallet', downloadOptions.wallet)}
          />
          {!disableDeedDownload && (
            <DownloadButton
              type="deed"
              url={getGeneratedImageUrl('deed', downloadOptions.deed)}
            />
          )}
        </Flex>
      ) : (
        <DownloadButton
          type="redeem"
          url={getGeneratedImageUrl(translate[type], downloadOptions[type])}
        />
      )}
    </>
  );
};

const Share = () => {
  const {userId} = useModalContext();

  return (
    <Flex w="fit-content">
      <Flex maxW={{base: '25px', md: '33px'}} mr={{base: '4px', md: '16px'}}>
        <FacebookShareButton
          url={`${baseUrl}/collection/${userId}`}
          quote="I just bought a piece of the 1c Magenta"
          hashtag="#1CMagenta"
        >
          <FacebookIcon size="100%" round />
        </FacebookShareButton>
      </Flex>
      <Flex maxW={{base: '25px', md: '33px'}} mr={{base: '4px', md: '16px'}}>
        <TwitterShareButton
          title="I just bought a piece of the 1c Magenta"
          url={`${baseUrl}/collection/${userId}`}
          hashtags={['Showpiece', '1CMagenta', 'Philately']}
        >
          <TwitterIcon size="100%" round />
        </TwitterShareButton>
      </Flex>
      <Flex maxW={{base: '25px', md: '33px'}}>
        <WhatsappShareButton
          title="I just bought a piece of the 1c Magenta"
          url={`${baseUrl}/collection/${userId}`}
        >
          <WhatsappIcon size="100%" round />
        </WhatsappShareButton>
      </Flex>
    </Flex>
  );
};

const components: Record<string, JSX.Element> = {
  isShareable: <Share />,
  isDownloadable: <Download />,
  isPrintable: <Print />,
};

export const Actions: FC<{
  action: {
    isShareable?: boolean;
    isDownloadable?: boolean;
    isPrintable?: boolean;
  };
}> = ({action}) => {
  const justifyContent =
    action.isShareable || (action.isDownloadable && action.isPrintable)
      ? 'space-between'
      : 'flex-end';

  return (
    <Flex alignItems="center">
      <Flex justifyContent={justifyContent} w="fit-content">
        {Object.keys(action).map((act) => {
          return <React.Fragment key={act}>{components[act]}</React.Fragment>;
        })}
      </Flex>
    </Flex>
  );
};
